import { useState, useEffect, useRef } from "react";

interface UseConversationScrollDetectionProps {
  followUpQuestions: string[];
  isActivelyStreaming: boolean;
  conversationHistoryLength: number;
  showWelcome: boolean;
  searchInputValue: string;
}

export const useConversationScrollDetection = ({
  followUpQuestions,
  isActivelyStreaming,
  conversationHistoryLength,
  showWelcome,
  searchInputValue,
}: UseConversationScrollDetectionProps) => {
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Determine if suggestions should be available
  const shouldShowSuggestions =
    !showWelcome &&
    conversationHistoryLength > 0 &&
    !isActivelyStreaming &&
    followUpQuestions.length > 0;

  // Use intersection observer to detect when bottom element is visible
  useEffect(() => {
    if (!shouldShowSuggestions || !bottomRef.current) {
      setIsAtBottom(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const isVisible = entry.isIntersecting;
        setIsAtBottom(isVisible);
      },
      {
        threshold: 0.1, // Trigger when 10% of the element is visible
        rootMargin: "0px 0px 100px 0px", // 100px margin below viewport
      }
    );

    observer.observe(bottomRef.current);

    return () => {
      observer.disconnect();
    };
  }, [shouldShowSuggestions]);

  // Show suggestions when at bottom OR when input is focused
  const showSuggestions =
    shouldShowSuggestions && (isAtBottom || isInputFocused);

  // Set up input focus/blur listeners
  useEffect(() => {
    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement;

      // Check if the focused element is an input or textarea
      if (
        target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA")
      ) {
        setIsInputFocused(true);
      }
    };

    const handleFocusOut = (event: FocusEvent) => {
      const target = event.target as HTMLElement;

      // Check if the focused element is an input or textarea
      if (
        target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA")
      ) {
        setIsInputFocused(false);
      }
    };

    // Add focus listeners
    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("focusout", handleFocusOut);

    return () => {
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("focusout", handleFocusOut);
    };
  }, []);

  return {
    showSuggestions,
    shouldShowSuggestions,
    bottomRef, // This ref should be attached to an element at the bottom of the conversation
  };
};
